import React, { useState } from 'react';
import './ContinuousDiscovery.css';
import { ReactComponent as SpiralSVG } from '../assets/ContinuousDiscoverySVG.svg';

const INFO = {
  empowered: {
    label: 'Empowered team',
    dot: '#1AB96D',
    text: "At the heart of continuous discovery and delivery sits an empowered, cross-functional team. PM, designer and engineers work as one unit — not in handoffs, but in constant collaboration. The team owns the problem, not just the solution.",
    items: [
      'Shared ownership of outcomes, not just output',
      'Engineers involved in discovery, not just delivery',
      'Designers and PMs part of delivery decisions',
      'Team sets its own goals aligned with company strategy',
      'Psychological safety to experiment and learn from failure',
    ]
  },
  discovery: {
    label: 'Discovery — Continuous exploration',
    dot: '#F0D98A',
    borderClass: 'cd-info-block--discovery',
    text: "Discovery runs in parallel with delivery — not before it. The team continuously explores user needs, validates assumptions and prototypes solutions. This keeps us learning at all times, regardless of which sprint we're in.",
    items: [
      'User interviews and usability testing',
      'Problem validation and opportunity sizing',
      'Prototyping and concept testing',
      'Close collaboration with engineering throughout',
    ]
  },
  delivery: {
    label: 'Delivery — Continuous building',
    dot: '#6B7FD4',
    borderClass: 'cd-info-block--delivery',
    text: "Delivery is not a separate phase — it's a continuous stream of incremental value. The team builds, tests and ships with high quality, and brings real-world insights back into discovery to close the loop.",
    items: [
      'Incremental, shippable increments',
      'High quality — tested and observable',
      'Feedback loops back into discovery',
      'Shared ownership across PM, design and engineering',
    ]
  }
};

function ContinuousDiscovery() {
  const [active, setActive] = useState(null);

  const handleLabelClick = (id) => setActive(prev => prev === id ? null : id);

  return (
    <div className="cd-section">

      <div className="cd-spiral-box">
        <SpiralSVG
          className="cd-spiral-svg"
          aria-label="Continuous discovery and delivery spiral"
          onClick={(e) => {
            const el = e.target.closest('#cd-label-discovery, #cd-label-delivery, #cd-char-left, #cd-char-right, #cd-center');
            if (!el) return;
            if (el.id === 'cd-center') { handleLabelClick('empowered'); return; }
            const isDiscovery = el.id === 'cd-label-discovery' || el.id === 'cd-char-left';
            handleLabelClick(isDiscovery ? 'discovery' : 'delivery');
          }}
        />
      </div>

      {active && (
        <div className="popup-overlay" onClick={() => setActive(null)}>
          <div className="popup-content" onClick={e => e.stopPropagation()}>
            <span className="close-button" onClick={() => setActive(null)}>&times;</span>
            <div className="popup-text-content">
              <h2>{INFO[active].label}</h2>
              <p>{INFO[active].text}</p>
              <ul>
                {INFO[active].items.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="cd-header">
        <h2 className="cd-title">Continuous discovery and delivery</h2>
        <p className="cd-lead">
          We move away from sequential phases towards a model where discovery and delivery happen{' '}
          <strong>in parallel and continuously</strong> — driven by the whole team.
        </p>
      </div>

      <div className="cd-cards">
        <div className="cd-card">
          <div className="cd-card-img-wrap">
            <img src="/img/explore-and-prioritize-uten-tekst.png" alt="Continuous discovery" className="cd-card-img" />
          </div>
          <div className="cd-card-body">
            <h3>Continuous discovery</h3>
            <p>We constantly learn about users and the market — regardless of which sprint we're in.</p>
          </div>
        </div>
        <div className="cd-card">
          <div className="cd-card-img-wrap">
            <img src="/img/fast-iterative-delivery.png" alt="Fast, iterative delivery" className="cd-card-img" />
          </div>
          <div className="cd-card-body">
            <h3>Fast, iterative delivery</h3>
            <p>With AI accelerating the build, our job is to make sure we always build the <em>right</em> things.</p>
          </div>
        </div>
        <div className="cd-card">
          <div className="cd-card-img-wrap">
            <img src="/img/team-working-together.png" alt="The whole team owns it" className="cd-card-img" />
          </div>
          <div className="cd-card-body">
            <h3>The whole team owns it</h3>
            <p>Not just PM, not just design — everyone contributes to discovery and delivery as one unified team.</p>
          </div>
        </div>
      </div>

      <div className="cd-quote-block">
        <p>«The biggest risk is not building the wrong thing — it's building something nobody wants.»</p>
        <cite>— Marty Cagan, Product Operating Model</cite>
      </div>

    </div>
  );
}

export default ContinuousDiscovery;
